<template>
   <div>
      <navbar-component />
      <main class="bg-blueGray-100">
         <!-- Hero: ICP problem → BlenderLink7 answer -->
         <section
            class="relative overflow-hidden pt-16 py-20 md:py-28"
            :style="{ backgroundColor: '#111827' }"
         >
            <div
               class="absolute inset-0 bg-center bg-cover"
               :style="{ backgroundImage: `url(${heroImage})`, opacity: 0.4 }"
            ></div>
            <div
               class="absolute inset-0"
               :style="{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }"
            ></div>
            <div class="container relative mx-auto px-4 md:px-6">
               <div class="max-w-3xl">
                  <span
                     class="inline-block bg-blue-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-5 shadow-sm"
                  >
                     Frac blender control
                  </span>
                  <h1
                     class="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-5"
                  >
                     BlenderLink7 powered by WaveEdge
                  </h1>
                  <p
                     class="text-blueGray-100 text-lg md:text-xl leading-relaxed max-w-2xl mb-4"
                  >
                     Today's blender has to flex across sand delivery methods,
                     chemical and additive packages, and automated treatment
                     plans—with less reliance on tribal knowledge and manual
                     intervention.
                  </p>
                  <p
                     class="text-blueGray-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8"
                  >
                     BlenderLink7 is built for that reality: configurable sand
                     strategies, liquid and dry additive control, plan-driven
                     automation from the van or the unit, and automated safety
                     strategies that match how you actually run the iron.
                  </p>
                  <div class="flex flex-wrap gap-3">
                     <router-link
                        :to="{ path: '/', hash: '#contact' }"
                        class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                     >
                        Contact AMI <i class="fas fa-arrow-right ml-2"></i>
                     </router-link>
                     <router-link
                        to="/waveedge"
                        class="inline-block bg-transparent hover:bg-blueGray-800 text-white font-bold py-3 px-6 rounded-lg border border-blueGray-500 transition-colors duration-200"
                     >
                        About WaveEdge
                     </router-link>
                  </div>
               </div>
            </div>
         </section>

         <!-- ICP problem cards -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Problems blender crews actually face
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     BlenderLink7 is aimed at the operational friction that shows
                     up when every pad looks different—and every stage still has
                     to hit rate, concentration, and safety targets.
                  </p>
               </div>
               <div class="grid md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
                  <div
                     v-for="item in painPoints"
                     :key="item.title"
                     class="bg-blueGray-900 rounded-xl p-6 border border-blueGray-700"
                  >
                     <div class="flex items-center gap-3 mb-3">
                        <div
                           class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                           :class="item.iconWrapClass"
                        >
                           <i :class="item.icon"></i>
                        </div>
                        <h3 class="text-xl font-bold text-white leading-tight">
                           {{ item.title }}
                        </h3>
                     </div>
                     <p class="text-blueGray-400 text-sm mb-3">
                        <span class="font-semibold text-blueGray-300"
                           >The challenge:</span
                        >
                        {{ item.challenge }}
                     </p>
                     <p class="text-blueGray-200 text-sm md:text-base">
                        <span class="font-semibold text-lightBlue-300"
                           >How BlenderLink7 helps:</span
                        >
                        {{ item.solution }}
                     </p>
                  </div>
               </div>
               <div class="text-center max-w-3xl mx-auto mt-12 mb-6">
                  <h3 class="text-2xl md:text-3xl font-bold text-white mb-3">
                     Same HMI—on the blender or from the van
                  </h3>
                  <p class="text-blueGray-300 text-base md:text-lg leading-relaxed">
                     Everything you see on these screens is available remotely
                     from the data van as well—so crews can monitor and take
                     control on the blender panel or from off the iron, without
                     a different app or workflow.
                  </p>
               </div>
               <div
                  class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900 max-w-5xl mx-auto"
               >
                  <img
                     :src="mixingImage"
                     alt="BlenderLink7 mixing page showing sand, additives, and tub control"
                     class="w-full h-auto block"
                  />
                  <p
                     class="p-3 text-center text-sm text-blueGray-400 tracking-wide"
                  >
                     Mixing page—sand, additives, and tub control in one view
                  </p>
               </div>
            </div>
         </section>

         <!-- Flexible sand -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Flexible sand control for how the truck is built
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     One product, multiple sand strategies—so you are not locked
                     into a single metering style when the next job changes the
                     iron.
                  </p>
               </div>
               <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto mb-10">
                  <div
                     v-for="strategy in sandStrategies"
                     :key="strategy.title"
                     class="bg-blueGray-800 rounded-xl p-5 border border-blueGray-700"
                  >
                     <h3 class="text-lg font-bold text-white mb-2">
                        {{ strategy.title }}
                     </h3>
                     <p class="text-blueGray-300 text-sm">{{ strategy.text }}</p>
                  </div>
               </div>
               <div class="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                  <div
                     v-for="shot in sandShots"
                     :key="shot.title"
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-800"
                  >
                     <img
                        :src="shot.image"
                        :alt="shot.alt"
                        class="w-full h-auto block"
                     />
                     <p
                        class="p-3 text-center text-sm text-blueGray-400 tracking-wide"
                     >
                        {{ shot.title }}
                     </p>
                  </div>
               </div>
               <p
                  class="text-center text-blueGray-400 text-sm md:text-base mt-8 max-w-3xl mx-auto"
               >
                  Auto feeders make up to the overall sand-rate target—so Manual
                  units can stay pinned while the rest trim. Less babysitting.
                  Clearer concentration control.
               </p>
            </div>
         </section>

         <!-- Chemicals & additives -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
                  <div>
                     <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                        Chemical and additive injection that keeps up
                     </h2>
                     <p class="text-blueGray-300 text-lg mb-5 leading-relaxed">
                        Liquid and dry additive packages change by basin, customer,
                        and stage. BlenderLink7 is built so operators can set,
                        follow, and adjust loadings without fighting a rigid
                        one-recipe HMI.
                     </p>
                     <ul class="space-y-2">
                        <li
                           v-for="bullet in additiveBullets"
                           :key="bullet"
                           class="flex items-start gap-3 text-blueGray-200"
                        >
                           <i
                              class="fas fa-check-circle text-emerald-400 mt-1 flex-shrink-0"
                           ></i>
                           <span>{{ bullet }}</span>
                        </li>
                     </ul>
                  </div>
                  <div class="space-y-6">
                     <div
                        v-for="shot in additiveShots"
                        :key="shot.title"
                        class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900"
                     >
                        <img
                           :src="shot.image"
                           :alt="shot.alt"
                           class="w-full h-auto block"
                        />
                        <p
                           class="p-3 text-center text-sm text-blueGray-400 tracking-wide"
                        >
                           {{ shot.title }}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <!-- Automated treatment plans -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
                  <div
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-800 lg:order-1"
                  >
                     <img
                        :src="treatmentImage"
                        alt="BlenderLink7 treatment plan automation"
                        class="w-full h-auto block"
                     />
                  </div>
                  <div class="lg:order-2">
                     <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                        Fully automated control from the treatment plan
                     </h2>
                     <p class="text-blueGray-300 text-lg mb-5 leading-relaxed">
                        When concentration and additive loadings follow the active
                        plan step, crews spend less time re-entering setpoints and
                        more time supervising the stage. That means less specialized
                        training pressure and fewer manual interventions mid-job.
                     </p>
                     <ul class="space-y-2">
                        <li
                           v-for="bullet in treatmentBullets"
                           :key="bullet"
                           class="flex items-start gap-3 text-blueGray-200"
                        >
                           <i
                              class="fas fa-check-circle text-emerald-400 mt-1 flex-shrink-0"
                           ></i>
                           <span>{{ bullet }}</span>
                        </li>
                     </ul>
                  </div>
               </div>
            </div>
         </section>

         <!-- Safety -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Enhanced safety—and automated safety strategies
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     BlenderLink7 includes configurable E-stop triggers and
                     actions so shutdown behavior is deliberate—not improvised
                     under pressure.
                  </p>
               </div>
               <div class="grid lg:grid-cols-2 gap-6 max-w-6xl mx-auto mb-8">
                  <div
                     v-for="shot in safetyShots"
                     :key="shot.title"
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900"
                  >
                     <img
                        :src="shot.image"
                        :alt="shot.alt"
                        class="w-full h-auto block"
                     />
                     <div class="p-4">
                        <h3 class="text-lg font-bold text-white mb-1">
                           {{ shot.title }}
                        </h3>
                        <p class="text-blueGray-300 text-sm">{{ shot.text }}</p>
                     </div>
                  </div>
               </div>
               <div class="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
                  <div
                     v-for="item in safetyPoints"
                     :key="item.title"
                     class="bg-blueGray-900 rounded-xl p-5 border border-blueGray-700"
                  >
                     <h3 class="text-lg font-bold text-white mb-2">
                        {{ item.title }}
                     </h3>
                     <p class="text-blueGray-300 text-sm">{{ item.text }}</p>
                  </div>
               </div>
            </div>
         </section>

         <!-- Local / remote + WaveEdge CTA -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="grid lg:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto mb-12">
                  <div
                     v-for="item in deployments"
                     :key="item.title"
                     class="bg-blueGray-800 p-6 md:p-7 rounded-xl border border-blueGray-700"
                  >
                     <div class="flex items-start">
                        <div
                           class="w-12 h-12 rounded-full flex items-center justify-center mr-4 border-2 flex-shrink-0"
                           :class="item.iconWrapClass"
                        >
                           <i :class="item.icon"></i>
                        </div>
                        <div>
                           <p
                              class="text-xs font-bold uppercase tracking-wide mb-1"
                              :class="item.labelClass"
                           >
                              {{ item.label }}
                           </p>
                           <h3 class="text-xl font-bold text-white">
                              {{ item.title }}
                           </h3>
                           <p class="text-blueGray-300 mt-2">{{ item.text }}</p>
                        </div>
                     </div>
                  </div>
               </div>

               <div
                  class="bg-blueGray-800 rounded-2xl p-8 md:p-10 border border-blueGray-700 text-center max-w-3xl mx-auto"
               >
                  <h3 class="text-2xl font-bold text-white mb-4">
                     Powered by WaveEdge
                  </h3>
                  <p class="text-blueGray-300 mb-6">
                     BlenderLink7 runs on the WaveEdge industrial runtime—with a
                     clear path into WaveData Cloud and WaveCommand7 when the
                     data van owns the plan.
                  </p>
                  <div class="flex flex-wrap gap-3 justify-center">
                     <router-link
                        to="/waveedge"
                        class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                     >
                        Learn more about WaveEdge
                     </router-link>
                     <router-link
                        to="/wavecommand7"
                        class="inline-block bg-transparent hover:bg-blueGray-700 text-white font-bold py-3 px-6 rounded-lg border border-blueGray-500 transition-colors duration-200"
                     >
                        See WaveCommand7
                     </router-link>
                  </div>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo, productJsonLd, breadcrumbJsonLd } from "@/utils/seo";
import heroImage from "@/assets/img/blenderlink7/op-home-mixing.png";
import sandImage from "@/assets/img/blenderlink7/op-sand-auger.png";
import sandStrategyConveyor from "@/assets/img/blenderlink7/op-sand-strategy-dry-conveyor.png";
import additivesImage from "@/assets/img/blenderlink7/op-additives.png";
import materialsImage from "@/assets/img/blenderlink7/settings-materials.png";
import treatmentImage from "@/assets/img/blenderlink7/op-treatment-plan.png";
import estopImage from "@/assets/img/blenderlink7/settings-estop.png";
import alarmsImage from "@/assets/img/blenderlink7/alarms.png";

export default {
   name: "BlenderLink7",
   components: {
      NavbarComponent,
      FooterComponent,
   },
   data() {
      return {
         heroImage,
         mixingImage: heroImage,
         treatmentImage,
         painPoints: [
            {
               title: "Sand control that keeps changing",
               icon: "fas fa-industry",
               challenge:
                  "Auger today, conveyor tomorrow, gates on another truck—operators need one system that flexes without a forklift rewrite.",
               solution:
                  "Configurable sand strategies (auger, dry/wet conveyor, gravity gate) with Auto makeup to the overall sand-rate target.",
               iconWrapClass: "bg-amber-900 text-amber-300 border-amber-400",
            },
            {
               title: "Chemical & additive complexity",
               icon: "fas fa-flask",
               challenge:
                  "Liquid and dry packages vary by customer and stage; rigid HMIs force workarounds and mistakes.",
               solution:
                  "Liquid and dry additive Manual / Auto paths, materials setup, and loadings that can follow the active treatment plan step.",
               iconWrapClass: "bg-emerald-900 text-emerald-300 border-emerald-500",
            },
            {
               title: "Too much manual intervention",
               icon: "fas fa-user-clock",
               challenge:
                  "Hitting rate and concentration by hand every step means more training load and more risk mid-stage.",
               solution:
                  "Plan-driven automation—run a local schedule or Auto-follow WaveCommand7 so setpoints track the stage with less babysitting.",
               iconWrapClass: "bg-blue-900 text-lightBlue-300 border-lightBlue-400",
            },
            {
               title: "Safety under pressure",
               icon: "fas fa-shield-alt",
               challenge:
                  "When something goes wrong, crews need predetermined shutdown behavior—not improvised button sequences.",
               solution:
                  "Configurable E-stop triggers mapped to Flush, Soft Stop, Hard Stop, or Off—plus clear alarms for fast acknowledgement.",
               iconWrapClass: "bg-orange-900 text-orange-300 border-orange-400",
            },
         ],
         sandStrategies: [
            {
               title: "Auger",
               text: "1–4 augers with per-auger Manual / Auto makeup to the overall sand-rate target.",
            },
            {
               title: "Dry conveyor",
               text: "Belt metering with Beltway, analog, or serial rate sources.",
            },
            {
               title: "Wet conveyor",
               text: "Same conveyor shell with moisture compensation for wet sand.",
            },
            {
               title: "Gravity gate",
               text: "Per-gate position control with the same Auto makeup behavior as augers.",
            },
         ],
         sandShots: [
            {
               title: "Sand / Auger control",
               alt: "BlenderLink7 sand and auger control",
               image: sandImage,
            },
            {
               title: "Dry conveyor strategy",
               alt: "BlenderLink7 dry conveyor sand strategy",
               image: sandStrategyConveyor,
            },
         ],
         additiveBullets: [
            "Liquid and dry additive setpoints with Manual / Auto",
            "Materials library for proppants and schedule additives",
            "Loadings that can track the active treatment plan step",
            "Less re-keying when the chemical package changes mid-job",
         ],
         additiveShots: [
            {
               title: "Additives control",
               alt: "BlenderLink7 liquid and dry additives",
               image: additivesImage,
            },
            {
               title: "Materials setup",
               alt: "BlenderLink7 materials configuration",
               image: materialsImage,
            },
         ],
         treatmentBullets: [
            "Plan Mode Auto follows WaveCommand7 from the van",
            "Plan Mode Manual for local schedule ownership on the blender",
            "Concentration and additive loadings can follow the live step",
            "Fewer mid-stage setpoint entries—more supervision, less typing",
         ],
         safetyShots: [
            {
               title: "Automated E-stop strategies",
               alt: "BlenderLink7 E-stop configuration",
               image: estopImage,
               text: "Map triggers like overpressure, tub overflow, and stop conditions to Flush, Soft Stop, Hard Stop, or Off.",
            },
            {
               title: "Alarms when action is needed",
               alt: "BlenderLink7 alarms screen",
               image: alarmsImage,
               text: "Acknowledge active alarms and review history so the crew stays aligned under stress.",
            },
         ],
         safetyPoints: [
            {
               title: "Predetermined actions",
               text: "Shutdown behavior is configured ahead of time—not invented mid-incident.",
            },
            {
               title: "Header E-stop menu",
               text: "Emergency Flush, Soft Stop, and Hard Stop available when the operator must act now.",
            },
            {
               title: "Login-gated commands",
               text: "Process writes and control handoff require a signed-in user—monitoring stays available.",
            },
         ],
         deployments: [
            {
               label: "Local HMI",
               title: "At the blender panel",
               icon: "fas fa-desktop",
               text: "Touch display on the control panel for day-to-day monitoring and control at the iron.",
               labelClass: "text-cyan-300",
               iconWrapClass: "bg-cyan-900 text-cyan-200 border-cyan-400",
            },
            {
               label: "Remote HMI",
               title: "Same app from the van",
               icon: "fas fa-cloud",
               text: "Open the same HMI remotely when the site allows—with clear Control vs View Only authority.",
               labelClass: "text-lightBlue-300",
               iconWrapClass:
                  "bg-blue-900 text-lightBlue-200 border-lightBlue-400",
            },
         ],
      };
   },
   mounted() {
      setPageSeo({
         title: "BlenderLink7 | Sand, Additives & Safety | AMI",
         description:
            "Flexible sand strategies, additive injection, treatment-plan automation, and configurable blender safety—powered by WaveEdge.",
         keywords:
            "BlenderLink7, BlenderLink, frac blender control, sand strategy, additive injection, treatment plan automation, blender safety, WaveEdge, AMI",
         path: "/blenderlink7",
         jsonLd: [
            productJsonLd({
               name: "BlenderLink7",
               description:
                  "Frac blender control with flexible sand strategies, additives, and automated safety.",
               path: "/blenderlink7",
            }),
            breadcrumbJsonLd([
               { name: "Home", path: "/" },
               { name: "BlenderLink7", path: "/blenderlink7" },
            ]),
         ],
      });
   },
};
</script>
